/**
 * Sticky Add to Cart Button
 * 
 * Displays a sticky add-to-cart button at the bottom of the viewport
 * when user scrolls past the product info section.
 * 
 * @class StickyAddToCart
 */
class StickyAddToCart {
  // Constants
  static SCROLL_THRESHOLD = 200; // px
  static DESKTOP_BREAKPOINT = 750; // px
  static POLL_INTERVAL = 100; // ms
  static UPDATE_DELAY = 100; // ms
  static CART_UPDATE_DELAY = 300; // ms

  /**
   * @param {HTMLElement} element - The sticky add-to-cart container element
   */
  constructor(element) {
    this.element = element;
    this.sectionId = element.dataset.section;
    
    // Cache DOM elements
    this.mainForm = document.querySelector(`#product-form-${this.sectionId}`);
    this.mainSubmitButton = this.mainForm?.querySelector('[type="submit"]');
    this.stickyButton = element.querySelector('.sticky-add-to-cart__button');
    this.stickyButtonText = element.querySelector('.sticky-add-to-cart__button-text');
    this.stickyPrice = document.querySelector(`#StickyPrice-${this.sectionId}`);
    this.mainPrice = document.querySelector(`#price-${this.sectionId}`);
    this.productInfo = document.querySelector(`#ProductInfo-${this.sectionId}`);
    // Find the product-form container element (custom element wrapping the form)
    this.productFormSection = this.mainForm?.closest('product-form') || this.mainForm;
    
    // State management
    this.checkInterval = null;
    this.isWatchingButton = false;
    this.scrollHandler = null;
    this.resizeHandler = null;
    this.priceObserver = null;
    
    // Event unsubscribers
    this.variantChangeUnsubscriber = null;
    this.cartUpdateUnsubscriber = null;
    this.cartErrorUnsubscriber = null;
    
    if (!this.mainForm || !this.stickyButton) {
      console.warn('StickyAddToCart: Required elements not found');
      return;
    }

    this.init();
  }

  /**
   * Initialize the component
   */
  init() {
    try {
      this.setupCartCloseListener();
      this.setupButtonClick();
      this.setupScrollListener();
      this.setupVariantChangeListener();
      this.setupPriceSync();
      this.setupEventSubscriptions();
      this.updateButtonState();
    } catch (error) {
      console.error('StickyAddToCart: Initialization error', error);
    }
  }

  /**
   * Setup event subscriptions for cart and variant updates
   */
  setupEventSubscriptions() {
    // Subscribe to variant change events
    this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      if (event.data?.sectionId !== this.sectionId) return;
      setTimeout(() => {
        this.updateButtonState();
        this.updatePrice();
      }, StickyAddToCart.UPDATE_DELAY);
    });

    // Subscribe to cart update events
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      this.handleCartUpdate();
    });

    // Subscribe to cart error events
    this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, () => {
      this.handleCartError();
    });
  }

  /**
   * Handle cart update event
   */
  handleCartUpdate() {
    setTimeout(() => {
      this.stopWatchingButton();
      this.removeLoadingState();
      this.updateButtonState();
    }, StickyAddToCart.CART_UPDATE_DELAY);
  }

  /**
   * Handle cart error event
   */
  handleCartError() {
    setTimeout(() => {
      this.stopWatchingButton();
      this.removeLoadingState();
      this.updateButtonState();
    }, StickyAddToCart.CART_UPDATE_DELAY);
  }

  /**
   * Remove loading state from sticky button
   */
  removeLoadingState() {
    if (this.stickyButton.classList.contains('loading')) {
      this.stickyButton.classList.remove('loading');
    }
  }

  /**
   * Setup scroll listener with throttling
   */
  setupScrollListener() {
    const handleScroll = throttle(() => {
      this.checkScrollPosition();
    }, 16); // ~60fps

    this.scrollHandler = handleScroll;
    this.resizeHandler = handleScroll;

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    
    // Initial check
    this.checkScrollPosition();
  }

  /**
   * Check scroll position and show/hide sticky button
   */
  checkScrollPosition() {
    try {
      // Check if sticky info column is enabled (desktop only)
      const isStickyInfoEnabled = window.innerWidth >= StickyAddToCart.DESKTOP_BREAKPOINT && 
        this.productInfo?.classList.contains('product__column-sticky');
      
      if (isStickyInfoEnabled) {
        this.hide();
        return;
      }
      
      const scrollY = window.scrollY || window.pageYOffset;
      
      // Check if product form section is visible in viewport
      // Use product form section instead of product info section
      const productFormBottom = this.productFormSection?.getBoundingClientRect().bottom || 0;
      
      // Show sticky button when scrolled past threshold and product form section is not visible
      if (scrollY > StickyAddToCart.SCROLL_THRESHOLD && productFormBottom < 0) {
        this.show();
      } else {
        this.hide();
      }
    } catch (error) {
      console.error('StickyAddToCart: Error checking scroll position', error);
    }
  }

  /**
   * Setup button click handler
   */
  setupButtonClick() {
    this.stickyButton.addEventListener('click', () => {
      try {
        if (this.mainSubmitButton && !this.mainSubmitButton.disabled) {
          // Trigger the main form submission
          this.mainSubmitButton.click();
          
          // Update sticky button state
          this.stickyButton.classList.add('loading');
          this.stickyButton.disabled = true;
          
          // Start watching main button state
          if (this.startWatchingButton) {
            this.startWatchingButton();
          }
        }
      } catch (error) {
        console.error('StickyAddToCart: Error handling button click', error);
        this.removeLoadingState();
      }
    });
  }

  /**
   * Setup variant change listener
   */
  setupVariantChangeListener() {
    const variantSelects = document.querySelector(`variant-selects[data-section="${this.sectionId}"]`);
    const variantRadios = document.querySelector(`variant-radios[data-section="${this.sectionId}"]`);
    
    const variantElement = variantSelects || variantRadios;
    if (variantElement) {
      variantElement.addEventListener('change', debounce(() => {
        this.updateButtonState();
        this.updatePrice();
      }, StickyAddToCart.UPDATE_DELAY));
    }
  }

  /**
   * Setup price synchronization using MutationObserver
   */
  setupPriceSync() {
    if (!this.mainPrice) return;

    this.priceObserver = new MutationObserver(() => {
      this.updatePrice();
    });
    
    this.priceObserver.observe(this.mainPrice, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  /**
   * Setup cart close listener - watches main button state
   */
  setupCartCloseListener() {
    if (!this.mainSubmitButton) return;

    const checkMainButtonState = () => {
      if (!this.isWatchingButton) return;
      
      try {
        const isLoading = this.mainSubmitButton.classList.contains('loading');
        const isDisabled = this.mainSubmitButton.disabled || 
                          this.mainSubmitButton.getAttribute('aria-disabled') === 'true';
        
        // If main button is ready, stop watching and update sticky button
        if (!isLoading && !isDisabled) {
          this.stopWatchingButton();
          this.removeLoadingState();
          this.updateButtonState();
        }
      } catch (error) {
        console.error('StickyAddToCart: Error checking button state', error);
        this.stopWatchingButton();
      }
    };

    this.startWatchingButton = () => {
      if (this.isWatchingButton) return;
      this.isWatchingButton = true;
      this.checkInterval = setInterval(checkMainButtonState, StickyAddToCart.POLL_INTERVAL);
      // Initial check after a short delay
      setTimeout(checkMainButtonState, StickyAddToCart.POLL_INTERVAL * 2);
    };

    this.stopWatchingButton = () => {
      this.isWatchingButton = false;
      if (this.checkInterval) {
        clearInterval(this.checkInterval);
        this.checkInterval = null;
      }
    };
  }

  /**
   * Update sticky button state to match main button
   */
  updateButtonState() {
    if (!this.mainSubmitButton) return;
    
    try {
      const isDisabled = this.mainSubmitButton.disabled || 
                         this.mainSubmitButton.getAttribute('aria-disabled') === 'true';
      const buttonText = this.mainSubmitButton.querySelector('span')?.textContent?.trim() || 
                        this.stickyButtonText.textContent;
      
      this.stickyButton.disabled = isDisabled;
      this.stickyButtonText.textContent = buttonText;
    } catch (error) {
      console.error('StickyAddToCart: Error updating button state', error);
    }
  }

  /**
   * Update sticky price to match main price
   */
  updatePrice() {
    if (!this.mainPrice || !this.stickyPrice) return;
    
    try {
      const priceContent = this.mainPrice.innerHTML;
      if (priceContent) {
        this.stickyPrice.innerHTML = priceContent;
      }
    } catch (error) {
      console.error('StickyAddToCart: Error updating price', error);
    }
  }

  /**
   * Show sticky button with animation
   */
  show() {
    if (this.element.hasAttribute('hidden')) {
      // Remove hidden attribute to trigger slide-in animation
      this.element.removeAttribute('hidden');
      // Force reflow to ensure animation starts
      this.element.offsetHeight;
      // Add class to body for styling adjustments (e.g., chat widget positioning)
      document.body.classList.add('sticky-add-to-cart-visible');
      this.updateButtonState();
      this.updatePrice();
    }
  }

  /**
   * Hide sticky button with animation
   */
  hide() {
    if (!this.element.hasAttribute('hidden')) {
      // Add hidden attribute to trigger slide-out animation
      this.element.setAttribute('hidden', '');
      // Remove class from body
      document.body.classList.remove('sticky-add-to-cart-visible');
    }
  }

  /**
   * Cleanup method - removes all event listeners and observers
   */
  disconnect() {
    // Stop watching button
    this.stopWatchingButton();
    
    // Remove scroll/resize listeners
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
    }
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
    
    // Disconnect price observer
    if (this.priceObserver) {
      this.priceObserver.disconnect();
      this.priceObserver = null;
    }
    
    // Remove body class if sticky button was visible
    document.body.classList.remove('sticky-add-to-cart-visible');
    
    // Unsubscribe from events
    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
    }
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    if (this.cartErrorUnsubscriber) {
      this.cartErrorUnsubscriber();
    }
  }
}

// Initialize sticky add to cart on page load
document.addEventListener('DOMContentLoaded', () => {
  const stickyAddToCartElements = document.querySelectorAll('.sticky-add-to-cart');
  stickyAddToCartElements.forEach((element) => {
    if (!element.classList.contains('initialized')) {
      element.classList.add('initialized');
      new StickyAddToCart(element);
    }
  });
});
